import type { RubricCriterionKey } from '@/api/evaluator-portal';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  EVALUATOR_RUBRIC_CRITERIA,
  EVALUATOR_RUBRIC_LEVEL_LABELS,
  EVALUATOR_RUBRIC_SCORES,
  type RubricSelectionState,
} from './rubric-definition';

interface EvaluatorRubricDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rubricData: RubricSelectionState;
  readOnly?: boolean;
  onSelect: (criterionKey: RubricCriterionKey, score: number) => void;
}

export function EvaluatorRubricDetailModal({
  open,
  onOpenChange,
  rubricData,
  readOnly = false,
  onSelect,
}: EvaluatorRubricDetailModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[94vh] w-[98vw] max-w-[98vw] overflow-hidden border border-slate-200 p-0 sm:max-w-[98vw] xl:h-[92vh] xl:w-[min(98vw,1720px)] xl:max-w-[min(98vw,1720px)] 2xl:w-[1720px] 2xl:max-w-[1720px]">
        <DialogHeader className="border-b border-slate-200 px-6 py-4 text-left">
          <DialogTitle className="text-2xl font-bold text-[#0f2744]">Detalle de la rúbrica</DialogTitle>
          <DialogDescription>
            La selección hecha aquí se refleja inmediatamente en la rúbrica simple de la pantalla principal.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(94vh-148px)] overflow-x-auto overflow-y-auto px-2 py-3 sm:px-4 sm:py-4 xl:max-h-[calc(92vh-148px)]">
          <table className="min-w-[1600px] border-collapse text-sm text-slate-700 xl:min-w-[1660px]">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-10 border border-slate-300 bg-white px-4 py-3 text-left font-semibold text-[#0f2744]">
                  Criterios
                </th>
                {EVALUATOR_RUBRIC_SCORES.map((score) => (
                  <th
                    key={score}
                    className="sticky top-0 border border-slate-300 bg-white px-4 py-3 text-center font-semibold text-[#0f2744]"
                  >
                    {score} - {EVALUATOR_RUBRIC_LEVEL_LABELS[score]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {EVALUATOR_RUBRIC_CRITERIA.map((criterion) => (
                <tr key={criterion.key}>
                  <td className="sticky left-0 z-10 w-[360px] border border-slate-300 bg-slate-50 px-5 py-4 align-top">
                    <div className="space-y-2">
                      <p className="font-semibold text-[#0f2744]">{criterion.label}</p>
                      <p className="text-xs leading-5 text-slate-600">{criterion.description}</p>
                    </div>
                  </td>
                  {EVALUATOR_RUBRIC_SCORES.map((score) => {
                    const selected = rubricData[criterion.key] === score;
                    return (
                      <td key={`${criterion.key}-${score}`} className="border border-slate-300 align-top">
                        <button
                          type="button"
                          disabled={readOnly}
                          onClick={() => onSelect(criterion.key, score)}
                          className={`flex h-full min-h-[148px] w-[250px] items-start justify-start p-4 text-left text-[12.5px] leading-5 transition ${
                            readOnly
                              ? selected
                                ? 'bg-emerald-100 text-emerald-900'
                                : 'bg-slate-50 text-slate-600'
                              : selected
                                ? 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-500'
                                : 'bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {criterion.levels[score]}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {readOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)} className="bg-[#0f2744] text-white hover:bg-[#17345b]">
            Aceptar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



